import React from 'react';
import { Store } from '../types';
import { X, MapPin, Store as StoreIcon, Phone, Clock } from 'lucide-react';
import { formatStoreOperationSummary } from '../utils/storeOperation';

interface StoreInfoModalProps {
    isOpen: boolean;
    onClose: () => void;
    store: Store | null;
}

const StoreInfoModal: React.FC<StoreInfoModalProps> = ({ isOpen, onClose, store }) => {
    if (!isOpen || !store) return null;
    const operationSummary = formatStoreOperationSummary(store);
    const sellerImage = store.owner?.image;

    return (
        <div className="fixed inset-0 z-[1002] flex justify-center items-end bg-black/50 backdrop-blur-sm sm:items-center">
            {/* Click outside to close */}
            <div className="absolute inset-0" onClick={onClose} />

            <div className="relative w-full sm:w-[500px] bg-white rounded-t-[32px] sm:rounded-[32px] overflow-hidden flex flex-col shadow-2xl animate-in slide-in-from-bottom-5 sm:slide-in-from-bottom-0 sm:zoom-in-95 duration-300">
                {/* Header */}
                <div className="p-6 pb-4 flex justify-between items-center border-b">
                    <h2 className="text-xl font-black text-gray-900 tracking-tight">매장 정보</h2>
                    <button
                        onClick={onClose}
                        className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {sellerImage && (
                        <div className="overflow-hidden rounded-[28px] border border-gray-100 bg-gray-50">
                            <img
                                src={sellerImage}
                                alt={store.owner?.businessName || store.name}
                                className="w-full h-48 object-cover"
                            />
                        </div>
                    )}

                    <div className="space-y-4">
                        {/* Business Name */}
                        <div className="flex gap-4 p-4 bg-gray-50 rounded-2xl">
                            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600 shrink-0">
                                <StoreIcon size={20} />
                            </div>
                            <div>
                                <h3 className="text-xs font-black text-gray-400 uppercase tracking-tighter mb-1">상호명</h3>
                                <p className="text-[15px] font-black text-gray-900 leading-tight">
                                    {store.owner?.businessName || store.name}
                                </p>
                                <p className="text-[11px] font-bold text-gray-400 mt-1">
                                    {store.orderType === 'STORE' ? '매장형 주문' : '배송지형 주문'}
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-4 p-4 bg-gray-50 rounded-2xl">
                            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600 shrink-0">
                                <Clock size={20} />
                            </div>
                            <div>
                                <h3 className="text-xs font-black text-gray-400 uppercase tracking-tighter mb-1">
                                    {store.orderType === 'STORE' ? '운영 안내' : '배송 안내'}
                                </h3>
                                <p className="text-[15px] font-black text-gray-900 leading-tight">
                                    {operationSummary}
                                </p>
                                {store.deliverySchedule && (
                                    <p className="text-[11px] font-bold text-gray-400 mt-1">
                                        {store.deliverySchedule}
                                    </p>
                                )}
                                {store.description && (
                                    <p className="text-[12px] font-bold text-gray-500 mt-2 whitespace-pre-line leading-relaxed">
                                        {store.description}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Address */}
                        <div className="flex gap-4 p-4 bg-gray-50 rounded-2xl">
                            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600 shrink-0">
                                <MapPin size={20} />
                            </div>
                            <div>
                                <h3 className="text-xs font-black text-gray-400 uppercase tracking-tighter mb-1">가게 주소</h3>
                                <p className="text-[15px] font-black text-gray-900 leading-tight">
                                    {store.owner?.storeAddress || '등록된 주소가 없습니다.'}
                                </p>
                            </div>
                        </div>

                        {/* Contact */}
                        <div className="flex gap-4 p-4 bg-gray-50 rounded-2xl items-center">
                            <div className="flex gap-4 items-start flex-1">
                                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600 shrink-0">
                                    <Phone size={20} />
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-xs font-black text-gray-400 uppercase tracking-tighter mb-1">연락처</h3>
                                    <p className="text-[15px] font-black text-gray-900 leading-tight">
                                        {store.owner?.storeContact || '등록된 연락처가 없습니다.'}
                                    </p>
                                </div>
                            </div>
                            {store.owner?.storeContact && (
                                <a
                                    href={`tel:${store.owner.storeContact}`}
                                    className="h-10 px-4 flex items-center justify-center bg-gray-900 text-white text-[13px] font-bold rounded-xl active:scale-95 transition-all shrink-0"
                                >
                                    전화하기
                                </a>
                            )}
                        </div>

                    </div>
                </div>

                {/* Footer Footer Footer */}
                <div className="p-6 pt-2 border-t bg-gray-50">
                    <button
                        onClick={onClose}
                        className="w-full h-[52px] bg-gray-900 text-white rounded-2xl font-black text-[15px] hover:bg-gray-800 transition-colors shadow-lg shadow-gray-900/10 active:scale-[0.98]"
                    >
                        확인
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StoreInfoModal;
